/**
 * Footer Component
 *
 * Application footer with links to legal pages.
 * Displayed on all pages (auth and main layouts).
 *
 * @module components/layout/Footer
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { legalPagesService } from '../../services/legal-pages.service';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [contactEnabled, setContactEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const checkContact = async () => {
      const enabled = await legalPagesService.isContactEnabled();
      setContactEnabled(enabled);
    };
    checkContact();
  }, []);

  return (
    <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Copyright */}
          <p className="text-sm text-slate-500 dark:text-slate-400">
            &copy; {currentYear} Budgira. {t('footer.allRightsReserved', 'Tous droits réservés.')}
          </p>

          {/* Legal links */}
          <nav className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link
              to="/about"
              className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            >
              {t('footer.about')}
            </Link>
            {contactEnabled && (
              <Link
                to="/contact"
                className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                {t('footer.contact')}
              </Link>
            )}
            <Link
              to="/terms"
              className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            >
              {t('footer.terms')}
            </Link>
            <Link
              to="/privacy"
              className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            >
              {t('footer.privacy')}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
